import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Descriptions,
  Alert,
  Popconfirm,
  Divider,
  Switch,
  Tabs,
  Spin,
  Progress,
  Grid,
  Typography,
  Tooltip,
  InputNumber,
  Row,
  Col,
  Collapse
} from 'antd';
import {
  LockOutlined,
  ReloadOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  SafetyCertificateOutlined,
  UploadOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  DownloadOutlined,
  EyeOutlined,
  StopOutlined,
  MinusCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import sslService from '../../services/sslService';

import { adminPath } from '../../../shared/config/paths';

const { Option } = Select;
const { TextArea } = Input;
const { useBreakpoint } = Grid;
const { Text } = Typography;

// 自动证书 CA(domains.acme_ca;null = Let's Encrypt)
const ACME_CAS = [
  { value: 'letsencrypt', label: "Let's Encrypt" },
  { value: 'zerossl', label: 'ZeroSSL' },
];

const ensureArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
};

// SSL Mode descriptions (mirrors user SslSettings)
const SSL_MODES = [
  { value: 'off', label: '关闭', color: '#f5222d', description: '不使用加密，CDN 到源站使用 HTTP 明文连接。' },
  { value: 'flexible', label: '灵活', color: '#fa8c16', description: '客户端到 CDN 使用 HTTPS，CDN 到源站使用 HTTP。源站无需配置 SSL 证书。' },
  { value: 'full', label: '完全', color: '#1890ff', description: 'CDN 到源站使用 HTTPS，但不验证源站证书。源站可以使用自签名证书。' },
  { value: 'full_strict', label: '完全（严格）', color: '#52c41a', description: 'CDN 到源站使用 HTTPS 并验证证书有效性。源站需要使用由受信 CA 签发的证书，或使用平台 Origin CA 证书。' }
];

const linesJoin = (arr) => (Array.isArray(arr) ? arr.join('\n') : (arr || ''));
const linesToArr = (s) => (s || '').split('\n').map((x) => x.trim()).filter(Boolean);

const FRAME_OPTIONS = [
  { value: '', label: '不设置' },
  { value: 'SAMEORIGIN', label: 'SAMEORIGIN（仅同源可嵌入）' },
  { value: 'DENY', label: 'DENY（禁止任何嵌入）' },
];

const REFERRER_POLICIES = [
  { value: '', label: '不设置' },
  { value: 'no-referrer', label: 'no-referrer' },
  { value: 'no-referrer-when-downgrade', label: 'no-referrer-when-downgrade' },
  { value: 'origin', label: 'origin' },
  { value: 'origin-when-cross-origin', label: 'origin-when-cross-origin' },
  { value: 'same-origin', label: 'same-origin' },
  { value: 'strict-origin', label: 'strict-origin' },
  { value: 'strict-origin-when-cross-origin', label: 'strict-origin-when-cross-origin' },
  { value: 'unsafe-url', label: 'unsafe-url' },
];

const CORS_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

const headersToForm = (headers) => {
  headers = headers || {};
  const hsts = headers.hsts || {};
  const sec = headers.security || {};
  const cors = headers.cors || {};
  return {
    hsts: {
      enabled: !!hsts.enabled,
      max_age: hsts.max_age ?? 31536000,
      include_sub_domains: !!hsts.include_sub_domains,
      preload: !!hsts.preload,
    },
    security: {
      enabled: !!sec.enabled,
      frame_options: sec.frame_options || '',
      content_type_options: !!sec.content_type_options,
      referrer_policy: sec.referrer_policy || '',
      csp: sec.csp || '',
    },
    cors: {
      enabled: !!cors.enabled,
      allow_origins: linesJoin(cors.allow_origins),
      allow_methods: ensureArray(cors.allow_methods),
      allow_headers: linesJoin(cors.allow_headers),
      expose_headers: linesJoin(cors.expose_headers),
      allow_credentials: !!cors.allow_credentials,
      max_age: cors.max_age ?? 0,
    },
    // 自定义响应头 set/add/remove → settings.security.headers.{set,add,remove}
    set: Array.isArray(headers.set) ? headers.set.map((h) => ({ name: h.name, value: h.value })) : [],
    add: Array.isArray(headers.add) ? headers.add.map((h) => ({ name: h.name, value: h.value })) : [],
    remove: Array.isArray(headers.remove) ? headers.remove : [],
  };
};

const buildHeadersPayload = (v) => {
  v = v || {};
  const hsts = v.hsts || {};
  const sec = v.security || {};
  const cors = v.cors || {};
  return {
    hsts: {
      enabled: !!hsts.enabled,
      max_age: hsts.max_age || 0,
      include_sub_domains: !!hsts.include_sub_domains,
      preload: !!hsts.preload,
    },
    security: {
      enabled: !!sec.enabled,
      frame_options: sec.frame_options || '',
      content_type_options: !!sec.content_type_options,
      referrer_policy: sec.referrer_policy || '',
      csp: sec.csp || '',
    },
    cors: {
      enabled: !!cors.enabled,
      allow_origins: linesToArr(cors.allow_origins),
      allow_methods: ensureArray(cors.allow_methods),
      allow_headers: linesToArr(cors.allow_headers),
      expose_headers: linesToArr(cors.expose_headers),
      allow_credentials: !!cors.allow_credentials,
      max_age: cors.max_age || 0,
    },
    // 自定义响应头 set/add/remove
    set: (v.set || []).filter((h) => h && h.name).map((h) => ({ name: h.name, value: h.value || '' })),
    add: (v.add || []).filter((h) => h && h.name).map((h) => ({ name: h.name, value: h.value || '' })),
    remove: (v.remove || []).filter(Boolean),
  };
};

export default function SslSettings() {
  const { domainData, reloadDomain } = useOutletContext();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);

  // Domain-level switches
  const [httpEnabled, setHttpEnabled] = useState(true);
  const [sslEnabled, setSslEnabled] = useState(false);
  const [forceHttps, setForceHttps] = useState(false);
  const [acmeCa, setAcmeCa] = useState('letsencrypt');
  const [acmeCaSaving, setAcmeCaSaving] = useState(false);
  const [sslMode, setSslMode] = useState('full');
  const [sslModeLoading, setSslModeLoading] = useState(false);

  // Security response headers (HSTS / security headers / CORS) → settings.security.headers
  const [headersForm] = Form.useForm();
  const [headersSaving, setHeadersSaving] = useState(false);

  // Per-hostname SSL modal
  const [sslModalVisible, setSslModalVisible] = useState(false);
  const [sslHostname, setSslHostname] = useState(null);
  const [sslLoading, setSslLoading] = useState(false);
  const [sslStatus, setSslStatus] = useState(null);
  const [sslForm] = Form.useForm();
  const sslPollingRef = useRef(null);

  // Certificate library state
  const [certLibrary, setCertLibrary] = useState([]);
  const [certLibraryLoading, setCertLibraryLoading] = useState(false);
  const [applyingCertId, setApplyingCertId] = useState(null);

  // Origin CA state (mirrors user SslSettings Origin CA card)
  const [originCerts, setOriginCerts] = useState([]);
  const [originCertsLoading, setOriginCertsLoading] = useState(false);
  const [originCertModalVisible, setOriginCertModalVisible] = useState(false);
  const [originCertCreateLoading, setOriginCertCreateLoading] = useState(false);
  const [originCertForm] = Form.useForm();
  const [viewCertModal, setViewCertModal] = useState(null);

  useEffect(() => {
    if (domainData) {
      setHttpEnabled(domainData.http_enabled !== undefined ? !!domainData.http_enabled : true);
      setSslEnabled(!!domainData.ssl_enabled);
      setForceHttps(!!domainData.force_https);
      setAcmeCa(domainData.acme_ca || 'letsencrypt');
      setSslMode(domainData.ssl_mode || 'full');
      headersForm.setFieldsValue(headersToForm(domainData?.settings?.security?.headers));
      loadOriginCerts();
    }
  }, [domainData]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (sslPollingRef.current) {
        clearInterval(sslPollingRef.current);
      }
    };
  }, []);

  // SSL 加密模式
  const handleSslModeSelect = (mode) => {
    setSslMode(mode);
  };

  const sslModeChanged = sslMode !== (domainData?.ssl_mode || 'full');

  const handleSslModeSave = async () => {
    if (!sslModeChanged) return;
    setSslModeLoading(true);
    try {
      const response = await cdnService.updateDomain({ id: domainData.id, ssl_mode: sslMode });
      if (response.success) {
        message.success('加密模式已更新');
        reloadDomain();
      } else {
        message.error(response.error || '更新失败');
      }
    } catch (error) {
      message.error('更新失败');
    } finally {
      setSslModeLoading(false);
    }
  };

  // 安全响应头（HSTS / 安全头 / CORS）
  const handleSaveHeaders = async () => {
    setHeadersSaving(true);
    try {
      const values = await headersForm.validateFields();
      const headers = buildHeadersPayload(values);
      const response = await cdnService.updateDomain({ id: domainData.id, security: { headers } });
      if (response.success) {
        message.success('安全响应头已保存');
        reloadDomain();
      } else {
        message.error(response.error || '保存失败');
      }
    } catch (error) {
      if (error && error.errorFields) return;
      message.error('保存失败');
    } finally {
      setHeadersSaving(false);
    }
  };

  // Domain-level toggle handlers
  const handleHttpToggle = async (checked) => {
    // Prevent disabling both
    if (!checked && !sslEnabled) {
      message.warning('HTTP 和 HTTPS 至少需要启用一个');
      return;
    }
    try {
      const response = await cdnService.updateDomain({
        id: domainData.id,
        http_enabled: !!checked
      });
      if (response.success) {
        setHttpEnabled(checked);
        message.success(checked ? 'HTTP 已启用' : 'HTTP 已禁用');
        reloadDomain();
      } else {
        message.error(response.error || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleSslToggle = async (checked) => {
    // Prevent disabling both
    if (!checked && !httpEnabled) {
      message.warning('HTTP 和 HTTPS 至少需要启用一个');
      return;
    }
    try {
      const response = await cdnService.updateDomain({
        id: domainData.id,
        ssl_enabled: !!checked
      });
      if (response.success) {
        setSslEnabled(checked);
        if (!checked) setForceHttps(false);
        message.success(checked ? 'HTTPS 已启用' : 'HTTPS 已禁用');
        reloadDomain();
      } else {
        message.error(response.error || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleForceHttpsToggle = async (checked) => {
    try {
      const response = await cdnService.updateDomain({
        id: domainData.id,
        force_https: !!checked
      });
      if (response.success) {
        setForceHttps(checked);
        message.success(checked ? '强制 HTTPS 跳转已启用' : '强制 HTTPS 跳转已禁用');
        reloadDomain();
      } else {
        message.error(response.error || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleAcmeCaChange = async (value) => {
    const prev = acmeCa;
    setAcmeCa(value);
    setAcmeCaSaving(true);
    try {
      const response = await cdnService.updateDomain({ id: domainData.id, acme_ca: value });
      if (response.success) {
        message.success('已保存');
        reloadDomain();
      } else {
        setAcmeCa(prev);
        message.error(response.error || '保存失败');
      }
    } catch (error) {
      setAcmeCa(prev);
      message.error('保存失败');
    } finally {
      setAcmeCaSaving(false);
    }
  };

  const loadCertLibrary = async () => {
    setCertLibraryLoading(true);
    try {
      const userId = domainData?.user_id;
      const response = await cdnService.getCertLibrary(userId);
      if (response.success) {
        setCertLibrary(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load cert library:', error);
    } finally {
      setCertLibraryLoading(false);
    }
  };

  const handleApplyCert = async (certId) => {
    if (!sslHostname) return;
    setApplyingCertId(certId);
    try {
      const response = await cdnService.applyCertToHostname(certId, sslHostname.id);
      if (response.success) {
        message.success('证书部署成功');
        reloadDomain();
        const statusResponse = await cdnService.getHostnameAcmeStatus(sslHostname.id);
        if (statusResponse.success) {
          setSslStatus(statusResponse.data);
        }
      } else {
        message.error(response.error || '部署失败');
      }
    } catch (error) {
      message.error('部署失败');
    } finally {
      setApplyingCertId(null);
    }
  };

  const getCompatibleCerts = () => {
    if (!sslHostname) return [];
    return certLibrary.filter(cert => {
      const domains = [cert.domain, ...ensureArray(cert.san_domains)];
      return domains.some(d => {
        if (d === sslHostname.hostname) return true;
        if (d.startsWith('*.')) {
          const base = d.substring(2);
          const parts = sslHostname.hostname.split('.');
          if (parts.length >= 2) {
            return parts.slice(1).join('.') === base;
          }
        }
        return false;
      });
    });
  };

  // Per-hostname SSL handlers
  const openSslModal = async (hostname) => {
    setSslHostname(hostname);
    setSslModalVisible(true);
    setSslLoading(true);
    try {
      const [statusResponse] = await Promise.all([
        cdnService.getHostnameAcmeStatus(hostname.id),
        loadCertLibrary()
      ]);
      if (statusResponse.success) {
        setSslStatus(statusResponse.data);
      }
    } catch (error) {
      console.error('Failed to get SSL status:', error);
    } finally {
      setSslLoading(false);
    }
  };

  const closeSslModal = () => {
    setSslModalVisible(false);
    setSslHostname(null);
    setSslStatus(null);
    if (sslPollingRef.current) {
      clearInterval(sslPollingRef.current);
      sslPollingRef.current = null;
    }
  };

  const handleEnableSsl = async (enabled, sslType = 'auto') => {
    if (!sslHostname) return;
    setSslLoading(true);
    try {
      const response = await cdnService.enableHostnameSsl(sslHostname.id, enabled, sslType);
      if (response.success) {
        message.success(enabled ? 'SSL 已启用' : 'SSL 已禁用');
        reloadDomain();
        const statusResponse = await cdnService.getHostnameAcmeStatus(sslHostname.id);
        if (statusResponse.success) {
          setSslStatus(statusResponse.data);
        }
      } else {
        message.error(response.error || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    } finally {
      setSslLoading(false);
    }
  };

  const handleRequestAcme = async (validationType = 'http-01') => {
    if (!sslHostname) return;
    setSslLoading(true);
    try {
      const response = await cdnService.requestHostnameAcme(sslHostname.id, validationType);
      if (response.success) {
        message.success('ACME 证书申请已提交');
        reloadDomain();
        startSslStatusPolling();
      } else {
        message.error(response.error || '申请失败');
      }
    } catch (error) {
      message.error('申请失败');
    } finally {
      setSslLoading(false);
    }
  };

  const startSslStatusPolling = () => {
    if (sslPollingRef.current) {
      clearInterval(sslPollingRef.current);
    }
    sslPollingRef.current = setInterval(async () => {
      if (!sslHostname) {
        clearInterval(sslPollingRef.current);
        return;
      }
      try {
        const response = await cdnService.getHostnameAcmeStatus(sslHostname.id);
        if (response.success) {
          setSslStatus(response.data);
          if (response.data.acme_status === 'issued' || response.data.acme_status === 'failed') {
            clearInterval(sslPollingRef.current);
            sslPollingRef.current = null;
            if (response.data.acme_status === 'issued') {
              message.success('SSL 证书已签发');
              reloadDomain();
            }
          }
        }
      } catch (error) {
        console.error('Failed to poll SSL status:', error);
      }
    }, 5000);
  };

  const handleUploadCert = async (values) => {
    if (!sslHostname) return;
    setSslLoading(true);
    try {
      const response = await cdnService.uploadHostnameSsl(
        sslHostname.id,
        values.certificate,
        values.private_key
      );
      if (response.success) {
        message.success('证书上传成功');
        reloadDomain();
        const statusResponse = await cdnService.getHostnameAcmeStatus(sslHostname.id);
        if (statusResponse.success) {
          setSslStatus(statusResponse.data);
        }
      } else {
        message.error(response.error || '上传失败');
      }
    } catch (error) {
      message.error('上传失败');
    } finally {
      setSslLoading(false);
    }
  };

  const getSslStatusTag = (hostname) => {
    const status = hostname.ssl_status;
    if (!status || !status.enabled) {
      return <Tag>未启用</Tag>;
    }
    const acmeStatus = status.acme_status;
    if (status.type === 'custom') {
      return <Tag color="green" icon={<CheckCircleOutlined />}>自定义证书</Tag>;
    }
    switch (acmeStatus) {
      case 'pending':
        return <Tag color="processing" icon={<LoadingOutlined />}>等待中</Tag>;
      case 'verifying':
        return <Tag color="processing" icon={<SyncOutlined spin />}>验证中</Tag>;
      case 'issuing':
        return <Tag color="processing" icon={<SyncOutlined spin />}>签发中</Tag>;
      case 'issued':
        return <Tag color="success" icon={<CheckCircleOutlined />}>已签发</Tag>;
      case 'failed':
        return <Tag color="error" icon={<CloseCircleOutlined />}>失败</Tag>;
      default:
        return <Tag color="blue" icon={<LockOutlined />}>已启用</Tag>;
    }
  };

  const getCertTypeLabel = (hostname) => {
    const status = hostname.ssl_status;
    if (!status || !status.enabled) return '无';
    if (status.type === 'custom') return '自定义';
    if (status.acme_status) return 'ACME';
    return '自动';
  };

  const getExpiresAt = (hostname) => {
    const status = hostname.ssl_status;
    if (!status || !status.certificate) return '-';
    return status.certificate.expires_at || '-';
  };

  // Per-hostname table columns
  const hostnameColumns = [
    {
      title: '主机名',
      dataIndex: 'hostname',
      key: 'hostname',
      render: (text) => <Text strong style={{ wordBreak: 'break-all' }}>{text}</Text>
    },
    {
      title: 'SSL 状态',
      key: 'ssl_status',
      width: 120,
      render: (_, record) => getSslStatusTag(record)
    },
    {
      title: '证书类型',
      key: 'cert_type',
      width: 100,
      render: (_, record) => getCertTypeLabel(record)
    },
    {
      title: '到期时间',
      key: 'expires_at',
      width: 160,
      render: (_, record) => getExpiresAt(record)
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<SafetyCertificateOutlined />}
            onClick={() => openSslModal(record)}
          >
            管理 SSL
          </Button>
        </Space>
      )
    }
  ];

  // Mobile card rendering for hostnames
  const renderHostnameCard = (item) => (
    <Card key={item.id} size="small" style={{ marginBottom: 12 }}>
      <div style={{ marginBottom: 8 }}>
        <Text strong style={{ wordBreak: 'break-all' }}>{item.hostname}</Text>
      </div>
      <Space wrap size="small" style={{ marginBottom: 8 }}>
        {getSslStatusTag(item)}
        <Tag>{getCertTypeLabel(item)}</Tag>
      </Space>
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        到期时间: {getExpiresAt(item)}
      </div>
      <Button
        size="small"
        icon={<SafetyCertificateOutlined />}
        onClick={() => openSslModal(item)}
      >
        管理 SSL
      </Button>
    </Card>
  );

  // ==========================================
  // Origin CA Certificates (admin, mirrors user)
  // ==========================================

  const loadOriginCerts = async () => {
    if (!domainData?.id) return;
    setOriginCertsLoading(true);
    try {
      const response = await cdnService.getOriginCerts(1, 50, { domain_id: domainData.id });
      if (response.success) {
        setOriginCerts(response.data?.items || []);
      }
    } catch (error) {
      console.error('Failed to load origin certs:', error);
    } finally {
      setOriginCertsLoading(false);
    }
  };

  const handleCreateOriginCert = async (values) => {
    setOriginCertCreateLoading(true);
    try {
      const response = await cdnService.createOriginCert({
        hostnames: values.hostnames,
        key_type: values.key_type || 'ecc256',
        validity_days: values.validity_days || 5475,
        domain_id: domainData?.id
      });
      if (response.success) {
        message.success('Origin CA 证书已签发');
        setOriginCertModalVisible(false);
        originCertForm.resetFields();
        setViewCertModal(response.data);
        loadOriginCerts();
      } else {
        message.error(response.error || '签发失败');
      }
    } catch (error) {
      message.error('签发失败');
    } finally {
      setOriginCertCreateLoading(false);
    }
  };

  const handleRevokeOriginCert = async (id) => {
    try {
      const response = await cdnService.revokeOriginCert(id);
      if (response.success) {
        message.success('证书已吊销');
        loadOriginCerts();
      } else {
        message.error(response.error || '吊销失败');
      }
    } catch (error) {
      message.error('吊销失败');
    }
  };

  const handleDeleteOriginCert = async (id) => {
    try {
      const response = await cdnService.deleteOriginCert(id);
      if (response.success) {
        message.success('证书已删除');
        loadOriginCerts();
      } else {
        message.error(response.error || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label}已复制到剪贴板`);
    }).catch(() => {
      message.error('复制失败');
    });
  };

  const handleDownloadCaCert = async () => {
    try {
      const response = await cdnService.getOriginCaCert();
      const pem = response?.data?.certificate;
      if (!response.success || !pem) {
        message.error(response.error || '获取 CA 根证书失败');
        return;
      }
      const blob = new Blob([pem], { type: 'application/x-pem-file' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'origin_ca_root.pem';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success('CA 根证书已下载');
    } catch (error) {
      message.error('下载失败');
    }
  };

  const openCreateOriginCertModal = () => {
    const domain = domainData?.domain || '';
    originCertForm.setFieldsValue({
      hostnames: domain ? [domain, `*.${domain}`] : [''],
      key_type: 'ecc256',
      validity_days: 5475
    });
    setOriginCertModalVisible(true);
  };

  const originCertColumns = [
    {
      title: '域名',
      key: 'hostnames',
      render: (_, record) => (
        <Space size={4} wrap>
          {ensureArray(record.hostnames).map(h => (
            <Tag key={h} style={{ fontSize: 12 }}>{h}</Tag>
          ))}
        </Space>
      )
    },
    {
      title: '密钥类型',
      dataIndex: 'key_type',
      key: 'key_type',
      width: 90,
      render: (v) => <Tag>{(v || '').toUpperCase()}</Tag>
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (v) => v === 'active'
        ? <Tag color="green">有效</Tag>
        : <Tag color="red">已吊销</Tag>
    },
    {
      title: '到期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 120,
      render: (v) => <Text style={{ fontSize: 12 }}>{(v || '').split(' ')[0]}</Text>
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="查看证书">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={async () => {
                const resp = await cdnService.getOriginCert(record.id);
                if (resp.success) {
                  setViewCertModal({ ...resp.data, private_key: null });
                }
              }}
            />
          </Tooltip>
          {record.status === 'active' && (
            <Popconfirm
              title="确认吊销此证书？"
              onConfirm={() => handleRevokeOriginCert(record.id)}
              okText="吊销"
              cancelText="取消"
            >
              <Button type="link" size="small" danger icon={<StopOutlined />} />
            </Popconfirm>
          )}
          <Popconfirm
            title="确认删除此证书？此操作不可撤销。"
            onConfirm={() => handleDeleteOriginCert(record.id)}
            okText="删除"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const renderOriginCertMobileCard = (cert) => (
    <Card key={cert.id} size="small" style={{ marginBottom: 12 }}>
      <div style={{ marginBottom: 8 }}>
        <Space size={4} wrap>
          {ensureArray(cert.hostnames).map(h => (
            <Tag key={h} style={{ fontSize: 11 }}>{h}</Tag>
          ))}
        </Space>
      </div>
      <div style={{ fontSize: 12, marginBottom: 8 }}>
        <Space wrap size="small">
          <Tag>{(cert.key_type || '').toUpperCase()}</Tag>
          {cert.status === 'active'
            ? <Tag color="green">有效</Tag>
            : <Tag color="red">已吊销</Tag>
          }
          <Text type="secondary">到期: {(cert.expires_at || '').split(' ')[0]}</Text>
        </Space>
      </div>
      <Space size={8}>
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={async () => {
            const resp = await cdnService.getOriginCert(cert.id);
            if (resp.success) {
              setViewCertModal({ ...resp.data, private_key: null });
            }
          }}
        >
          查看
        </Button>
        {cert.status === 'active' && (
          <Popconfirm
            title="确认吊销此证书？"
            onConfirm={() => handleRevokeOriginCert(cert.id)}
            okText="吊销"
            cancelText="取消"
          >
            <Button size="small" danger icon={<StopOutlined />}>吊销</Button>
          </Popconfirm>
        )}
        <Popconfirm
          title="确认删除此证书？"
          onConfirm={() => handleDeleteOriginCert(cert.id)}
          okText="删除"
          cancelText="取消"
        >
          <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      </Space>
    </Card>
  );

  const hostnames = domainData?.hostnames || [];

  return (
    <div style={{ padding: isMobile ? '12px 0' : '24px 0' }}>
      {/* SSL/TLS 加密模式 */}
      <Card
        title={<Space><SafetyCertificateOutlined /><span>SSL/TLS 加密模式</span></Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: isMobile ? 12 : 24 }}
      >
        <Spin spinning={sslModeLoading}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
            {SSL_MODES.map(mode => {
              const selected = sslMode === mode.value;
              return (
                <div
                  key={mode.value}
                  onClick={() => handleSslModeSelect(mode.value)}
                  style={{ padding: '10px 12px', border: `2px solid ${selected ? mode.color : '#d9d9d9'}`, borderRadius: 8, background: selected ? `${mode.color}0a` : '#fafafa', cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s' }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: mode.color, margin: '0 auto 6px' }} />
                  <Text strong style={{ fontSize: 13, color: selected ? mode.color : undefined }}>{mode.label}</Text>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 12px', background: '#f6f6f6', borderRadius: 6, fontSize: 13, color: '#555' }}>
            <span>{SSL_MODES.find(m => m.value === sslMode)?.description}</span>
            {sslModeChanged && (
              <Button type="primary" size="small" onClick={handleSslModeSave} loading={sslModeLoading}>保存</Button>
            )}
          </div>
        </Spin>
      </Card>

      {/* HSTS + 安全响应头 → settings.security.headers */}
      <Card
        title={<Space><SafetyCertificateOutlined /><span>HSTS 与安全响应头</span></Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: isMobile ? 12 : 24 }}
        extra={<Button type="primary" onClick={handleSaveHeaders} loading={headersSaving} size={isMobile ? 'small' : 'middle'}>保存</Button>}
      >
        <Alert
          message="这些响应头在边缘为客户端注入，用于加固浏览器侧安全。请谨慎开启 HSTS：一旦下发并被浏览器缓存，在 max-age 内将强制使用 HTTPS。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={headersForm} layout="vertical" size={isMobile ? 'small' : 'middle'}>
          <Divider orientation="left" style={{ margin: '4px 0 12px' }}>
            HSTS（HTTP 严格传输安全）
            <Form.Item name={['hsts', 'enabled']} valuePropName="checked" noStyle>
              <Switch size="small" checkedChildren="启用" unCheckedChildren="禁用" style={{ marginLeft: 12 }} />
            </Form.Item>
          </Divider>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name={['hsts', 'max_age']} label="max-age (秒)" tooltip="浏览器强制 HTTPS 的时长，常用 31536000（1 年）">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="31536000" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name={['hsts', 'include_sub_domains']} label="包含子域" valuePropName="checked" tooltip="对所有子域同样强制 HTTPS"><Switch /></Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name={['hsts', 'preload']} label="Preload" valuePropName="checked" tooltip="允许加入浏览器 HSTS 预载名单（需满足 preload 条件）"><Switch /></Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: '4px 0 12px' }}>
            安全响应头
            <Form.Item name={['security', 'enabled']} valuePropName="checked" noStyle>
              <Switch size="small" checkedChildren="启用" unCheckedChildren="禁用" style={{ marginLeft: 12 }} />
            </Form.Item>
          </Divider>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name={['security', 'frame_options']} label="X-Frame-Options" tooltip="防点击劫持：控制页面是否可被 iframe 嵌入">
                <Select options={FRAME_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name={['security', 'referrer_policy']} label="Referrer-Policy">
                <Select options={REFERRER_POLICIES} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name={['security', 'content_type_options']} label="X-Content-Type-Options: nosniff" valuePropName="checked" tooltip="禁止浏览器 MIME 嗅探"><Switch /></Form.Item>
            </Col>
          </Row>
          <Form.Item name={['security', 'csp']} label="Content-Security-Policy (CSP)" tooltip="内容安全策略，留空则不下发。示例：default-src 'self'">
            <TextArea rows={2} placeholder="default-src 'self'; img-src *" />
          </Form.Item>

          <Collapse
            ghost
            style={{ marginTop: 4 }}
            items={[{
              key: 'cors',
              // forceRender：CORS 子字段常驻注册，避免面板未展开时 validateFields 收不到已存值 → 保存被清空。
              forceRender: true,
              label: (
                <Space>
                  <span>CORS（跨域资源共享）</span>
                  <Form.Item name={['cors', 'enabled']} valuePropName="checked" noStyle>
                    <Switch size="small" checkedChildren="启用" unCheckedChildren="禁用" onClick={(c, e) => e && e.stopPropagation && e.stopPropagation()} />
                  </Form.Item>
                </Space>
              ),
              children: (
                <>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name={['cors', 'allow_origins']} label="允许来源 (Access-Control-Allow-Origin)" tooltip="每行一个，* 表示任意来源"><TextArea rows={3} placeholder={'https://example.com\n*'} /></Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name={['cors', 'allow_methods']} label="允许方法">
                        <Select mode="multiple" options={CORS_METHODS.map((m) => ({ value: m, label: m }))} placeholder="GET POST ..." />
                      </Form.Item>
                      <Form.Item name={['cors', 'max_age']} label="预检缓存 max-age (秒)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name={['cors', 'allow_headers']} label="允许请求头" tooltip="每行一个"><TextArea rows={2} placeholder={'Content-Type\nAuthorization'} /></Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name={['cors', 'expose_headers']} label="暴露响应头" tooltip="每行一个"><TextArea rows={2} /></Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name={['cors', 'allow_credentials']} label="允许携带凭证 (Allow-Credentials)" valuePropName="checked" tooltip="开启后不能与来源 * 同用"><Switch /></Form.Item>
                </>
              ),
            }]}
          />

          <Divider orientation="left" style={{ margin: '12px 0' }}>自定义响应头</Divider>
          <Alert
            message="直接控制边缘返回给客户端的响应头。set 覆盖同名头，add 追加（可重复），remove 删除指定头。"
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
          />

          <div style={{ marginBottom: 4, fontWeight: 500 }}>设置 / 覆盖（set）</div>
          <Form.List name="set">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={8} key={key} style={{ marginBottom: 8 }} align="middle">
                    <Col xs={10} sm={8}>
                      <Form.Item {...restField} name={[name, 'name']} style={{ marginBottom: 0 }} rules={[{ required: true, message: '头名称' }]}>
                        <Input placeholder="头名称，如 X-Frame-Options" />
                      </Form.Item>
                    </Col>
                    <Col xs={11} sm={14}>
                      <Form.Item {...restField} name={[name, 'value']} style={{ marginBottom: 0 }}>
                        <Input placeholder="头值" />
                      </Form.Item>
                    </Col>
                    <Col xs={3} sm={2}>
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ name: '', value: '' })} icon={<PlusOutlined />} block style={{ marginBottom: 16 }}>添加 set 头</Button>
              </>
            )}
          </Form.List>

          <div style={{ marginBottom: 4, fontWeight: 500 }}>追加（add）</div>
          <Form.List name="add">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={8} key={key} style={{ marginBottom: 8 }} align="middle">
                    <Col xs={10} sm={8}>
                      <Form.Item {...restField} name={[name, 'name']} style={{ marginBottom: 0 }} rules={[{ required: true, message: '头名称' }]}>
                        <Input placeholder="头名称，如 Set-Cookie" />
                      </Form.Item>
                    </Col>
                    <Col xs={11} sm={14}>
                      <Form.Item {...restField} name={[name, 'value']} style={{ marginBottom: 0 }}>
                        <Input placeholder="头值" />
                      </Form.Item>
                    </Col>
                    <Col xs={3} sm={2}>
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ name: '', value: '' })} icon={<PlusOutlined />} block style={{ marginBottom: 16 }}>添加 add 头</Button>
              </>
            )}
          </Form.List>

          <Form.Item name="remove" label="删除响应头（remove）" tooltip="边缘将从响应中移除这些头名（回车分隔）">
            <Select mode="tags" placeholder="要删除的头名，如 Server、X-Powered-By" style={{ width: '100%' }} />
          </Form.Item>

        </Form>
      </Card>

      {/* Domain-level HTTP/HTTPS switches */}
      <Card
        title={
          <Space>
            <LockOutlined />
            协议设置
          </Space>
        }
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 24 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>HTTP 监听 (Port 80)</Text>
                <div style={{ color: '#666', fontSize: 12 }}>允许客户端通过 HTTP 协议访问（不影响回源协议，回源协议在源站设置中配置）</div>
              </div>
              <Switch
                checked={httpEnabled}
                onChange={handleHttpToggle}
                checkedChildren="启用"
                unCheckedChildren="禁用"
              />
            </div>
            <Divider style={{ margin: '4px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>HTTPS 监听 (Port 443)</Text>
                <div style={{ color: '#666', fontSize: 12 }}>允许客户端通过 HTTPS 加密协议访问（不影响回源协议，回源协议在源站设置中配置）</div>
              </div>
              <Switch
                checked={sslEnabled}
                onChange={handleSslToggle}
                checkedChildren="启用"
                unCheckedChildren="禁用"
              />
            </div>
            {httpEnabled && sslEnabled && (
              <>
                <Divider style={{ margin: '4px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: 16 }}>
                  <div>
                    <Text>强制 HTTPS 跳转</Text>
                    <div style={{ color: '#666', fontSize: 12 }}>强制客户端使用 HTTPS 访问，将所有 HTTP 请求 301 跳转到 HTTPS</div>
                  </div>
                  <Switch
                    checked={forceHttps}
                    onChange={handleForceHttpsToggle}
                    checkedChildren="开"
                    unCheckedChildren="关"
                  />
                </div>
              </>
            )}
            <Divider style={{ margin: '4px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Tooltip title="下次签发/续期生效;签发失败自动改用另一家 CA">
                <Text strong>证书颁发机构</Text>
              </Tooltip>
              <Select
                value={acmeCa}
                onChange={handleAcmeCaChange}
                loading={acmeCaSaving}
                disabled={acmeCaSaving}
                options={ACME_CAS}
                style={{ width: 160 }}
              />
            </div>
          </Space>
        </div>
        <Alert
          message="以上设置仅控制客户端→CDN 方向（用户访问协议）。CDN→源站的回源协议请在「源站设置」中为每个源站单独配置。"
          type="info"
          style={{ marginTop: 12 }}
        />
        {!httpEnabled && !sslEnabled && (
          <Alert
            message="至少需要启用 HTTP 或 HTTPS 中的一个"
            type="error"
            showIcon
            style={{ marginTop: 8 }}
          />
        )}
      </Card>

      {/* Per-hostname SSL management */}
      <Card
        title={
          <Space>
            <SafetyCertificateOutlined />
            SSL/TLS 证书管理
          </Space>
        }
        size={isMobile ? 'small' : 'default'}
      >
        <Alert
          message="为每个源站主机名单独管理 SSL/TLS 证书"
          description="您可以为每个主机名分别申请或上传 SSL 证书。支持 Let's Encrypt 自动证书和自定义证书上传。"
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />

        {hostnames.length > 0 ? (
          isMobile ? (
            <div>
              {hostnames.map(item => renderHostnameCard(item))}
            </div>
          ) : (
            <Table
              columns={hostnameColumns}
              dataSource={hostnames}
              rowKey="id"
              pagination={false}
              size="small"
            />
          )
        ) : (
          <Alert
            message="暂无源站配置"
            description="请先在源站设置中添加主机名，然后再配置 SSL 证书"
            type="info"
            showIcon
          />
        )}
      </Card>

      {/* Origin CA 证书管理 */}
      <Card
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span>Origin CA 证书</span>
          </Space>
        }
        size={isMobile ? 'small' : 'default'}
        style={{ marginTop: 24 }}
        extra={
          <Space size={isMobile ? 4 : 8}>
            <Button
              icon={<DownloadOutlined />}
              size={isMobile ? 'small' : 'middle'}
              onClick={handleDownloadCaCert}
            >
              {isMobile ? 'CA' : '下载 CA 根证书'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size={isMobile ? 'small' : 'middle'}
              onClick={openCreateOriginCertModal}
            >
              {isMobile ? '签发' : '签发证书'}
            </Button>
          </Space>
        }
      >
        <Alert
          message="Origin CA 证书用于源站到 CDN 之间的加密"
          description={
            <span>
              为该域名的所有者签发 Origin CA 证书，安装在其源服务器上，配合「完全（严格）」加密模式使用。
              Origin CA 证书仅被本平台 CDN 信任，不被浏览器信任，因此仅用于 CDN 到源站的加密。
              证书最长有效期 15 年。
            </span>
          }
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />

        {originCertsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin />
          </div>
        ) : originCerts.length === 0 ? (
          <Alert
            message="暂无 Origin CA 证书"
            description="点击「签发证书」为该域名的源站生成 Origin CA 证书。"
            type="info"
            showIcon
          />
        ) : isMobile ? (
          <div>
            {originCerts.map(cert => renderOriginCertMobileCard(cert))}
          </div>
        ) : (
          <Table
            columns={originCertColumns}
            dataSource={originCerts}
            rowKey="id"
            pagination={false}
            size="small"
          />
        )}
      </Card>

      {/* SSL Management Modal (per-hostname) */}
      <Modal
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span style={{ fontSize: isMobile ? 14 : 16 }}>
              SSL 证书配置 - {sslHostname?.hostname}
            </span>
          </Space>
        }
        open={sslModalVisible}
        onCancel={closeSslModal}
        footer={null}
        width={isMobile ? '100%' : 700}
        style={isMobile ? { top: 20 } : undefined}
        styles={isMobile ? { body: { maxHeight: '70vh', overflow: 'auto' } } : undefined}
      >
        {sslLoading && !sslStatus ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>加载中...</div>
          </div>
        ) : (
          <div>
            {/* Current Status */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>当前状态: </Text>
                  {sslHostname && getSslStatusTag(sslHostname)}
                </div>
                {sslStatus?.certificate && (
                  <div>
                    <Text strong>证书到期: </Text>
                    <Text>{sslStatus.certificate.expires_at}</Text>
                  </div>
                )}
                {sslStatus?.acme_error && (
                  <Alert
                    type="error"
                    message="证书申请失败"
                    description={sslStatus.acme_error}
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
              </Space>
            </Card>

            {/* ACME Progress */}
            {sslStatus?.acme_status && ['pending', 'verifying', 'issuing'].includes(sslStatus.acme_status) && (
              <Card size="small" title="ACME 证书申请进度" style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Progress
                    percent={
                      sslStatus.acme_status === 'pending' ? 20 :
                      sslStatus.acme_status === 'verifying' ? 50 :
                      sslStatus.acme_status === 'issuing' ? 80 : 0
                    }
                    status="active"
                    size={isMobile ? 'small' : 'default'}
                  />
                  <div>
                    <Tag color="processing" icon={<SyncOutlined spin />}>
                      {sslStatus.acme_status === 'pending' ? '等待处理' :
                       sslStatus.acme_status === 'verifying' ? '域名验证中' :
                       sslStatus.acme_status === 'issuing' ? '证书签发中' : '处理中'}
                    </Tag>
                  </div>
                </Space>
              </Card>
            )}

            <Divider />

            <Tabs
              size={isMobile ? 'small' : 'default'}
              items={[
                {
                  key: 'auto',
                  label: isMobile ? '自动证书' : "自动证书 (Let's Encrypt)",
                  children: (
                    <div>
                      <Alert
                        message="免费自动证书"
                        description={isMobile
                          ? "使用 Let's Encrypt 自动申请证书，完全免费。"
                          : "使用 Let's Encrypt 自动申请和续期 SSL 证书，完全免费。证书有效期90天，系统会自动续期。"
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                          <Text strong>验证方式:</Text>
                          <div style={{ marginTop: 8 }}>
                            <Button
                              type="primary"
                              icon={<SafetyCertificateOutlined />}
                              loading={sslLoading}
                              onClick={() => handleRequestAcme('http-01')}
                              disabled={sslStatus?.acme_status && ['pending', 'verifying', 'issuing'].includes(sslStatus.acme_status)}
                              size={isMobile ? 'small' : 'middle'}
                            >
                              {sslStatus?.certificate ? '续费证书' : 'HTTP-01 验证'}
                            </Button>
                            <div style={{ marginTop: 4 }}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                需要域名已解析到 CDN
                              </Text>
                            </div>
                          </div>
                        </div>
                        {sslStatus?.ssl_enabled && (
                          <div style={{ marginTop: 16 }}>
                            <Button
                              danger
                              onClick={() => handleEnableSsl(false)}
                              loading={sslLoading}
                              size={isMobile ? 'small' : 'middle'}
                            >
                              禁用 SSL
                            </Button>
                          </div>
                        )}
                      </Space>
                    </div>
                  )
                },
                {
                  key: 'custom',
                  label: '自定义证书',
                  children: (
                    <div>
                      <Alert
                        message="上传自定义证书"
                        description="上传您自己购买的 SSL 证书。"
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                      <Form
                        form={sslForm}
                        layout="vertical"
                        onFinish={handleUploadCert}
                        size={isMobile ? 'middle' : 'default'}
                      >
                        <Form.Item
                          name="certificate"
                          label="证书 (PEM格式)"
                          rules={[{ required: true, message: '请输入证书内容' }]}
                        >
                          <TextArea
                            rows={isMobile ? 4 : 6}
                            placeholder="-----BEGIN CERTIFICATE-----"
                          />
                        </Form.Item>
                        <Form.Item
                          name="private_key"
                          label="私钥 (PEM格式)"
                          rules={[{ required: true, message: '请输入私钥内容' }]}
                        >
                          <TextArea
                            rows={isMobile ? 4 : 6}
                            placeholder="-----BEGIN PRIVATE KEY-----"
                          />
                        </Form.Item>
                        <Form.Item>
                          <Button
                            type="primary"
                            htmlType="submit"
                            icon={<UploadOutlined />}
                            loading={sslLoading}
                            size={isMobile ? 'small' : 'middle'}
                          >
                            上传证书
                          </Button>
                        </Form.Item>
                      </Form>
                    </div>
                  )
                },
                {
                  key: 'library',
                  label: (
                    <Space size={4}>
                      <FolderOpenOutlined />
                      <span>证书夹</span>
                    </Space>
                  ),
                  children: (
                    <div>
                      <Alert
                        message="从证书管理中选择"
                        description="选择 SSL 证书管理中已有的证书直接部署到此主机名。支持通配符证书一键部署到多个子域名。"
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                      {certLibraryLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                          <Spin />
                        </div>
                      ) : (() => {
                        const compatible = getCompatibleCerts();
                        const incompatible = certLibrary.filter(c => !compatible.includes(c));
                        return compatible.length === 0 && incompatible.length === 0 ? (
                          <Alert
                            message="暂无可用证书"
                            description="请先在「SSL 证书管理」中购买或上传证书。"
                            type="warning"
                            showIcon
                          />
                        ) : (
                          <div>
                            {compatible.length > 0 && (
                              <div style={{ marginBottom: 16 }}>
                                <Text strong style={{ marginBottom: 8, display: 'block' }}>匹配的证书</Text>
                                {compatible.map(cert => (
                                  <Card
                                    key={cert.id}
                                    size="small"
                                    style={{ marginBottom: 8 }}
                                    styles={{ body: { padding: '8px 12px' } }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <Text strong style={{ wordBreak: 'break-all' }}>{cert.domain}</Text>
                                        <div style={{ marginTop: 4 }}>
                                          <Space size={4} wrap>
                                            {ensureArray(cert.san_domains).slice(0, 3).map(d => (
                                              <Tag key={d} style={{ fontSize: 11 }}>{d}</Tag>
                                            ))}
                                            {ensureArray(cert.san_domains).length > 3 && (
                                              <Tag style={{ fontSize: 11 }}>+{ensureArray(cert.san_domains).length - 3}</Tag>
                                            )}
                                          </Space>
                                        </div>
                                        <div style={{ marginTop: 4 }}>
                                          <Text type="secondary" style={{ fontSize: 11 }}>
                                            {cert.issuer} · 到期 {(cert.expires_at || '').split(' ')[0]}
                                          </Text>
                                        </div>
                                      </div>
                                      <Button
                                        type="primary"
                                        size="small"
                                        loading={applyingCertId === cert.id}
                                        onClick={() => handleApplyCert(cert.id)}
                                      >
                                        部署
                                      </Button>
                                    </div>
                                  </Card>
                                ))}
                              </div>
                            )}
                            {incompatible.length > 0 && (
                              <div>
                                <Text type="secondary" style={{ marginBottom: 8, display: 'block', fontSize: 12 }}>
                                  不匹配的证书（域名不符）
                                </Text>
                                {incompatible.map(cert => (
                                  <Card
                                    key={cert.id}
                                    size="small"
                                    style={{ marginBottom: 8, opacity: 0.5 }}
                                    styles={{ body: { padding: '8px 12px' } }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <div>
                                        <Text style={{ wordBreak: 'break-all' }}>{cert.domain}</Text>
                                        <div style={{ marginTop: 2 }}>
                                          <Text type="secondary" style={{ fontSize: 11 }}>
                                            {cert.issuer} · 到期 {(cert.expires_at || '').split(' ')[0]}
                                          </Text>
                                        </div>
                                      </div>
                                      <Tooltip title="证书域名不匹配此主机名">
                                        <Button size="small" disabled>不匹配</Button>
                                      </Tooltip>
                                    </div>
                                  </Card>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )
                }
              ]}
            />
          </div>
        )}
      </Modal>

      {/* Origin CA Create Modal */}
      <Modal
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span>签发 Origin CA 证书</span>
          </Space>
        }
        open={originCertModalVisible}
        onCancel={() => { setOriginCertModalVisible(false); originCertForm.resetFields(); }}
        footer={null}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { top: 20 } : undefined}
      >
        <Form
          form={originCertForm}
          layout="vertical"
          onFinish={handleCreateOriginCert}
          initialValues={{
            key_type: 'ecc256',
            validity_days: 5475
          }}
        >
          <Form.List name="hostnames">
            {(fields, { add, remove }) => (
              <Form.Item label="域名 (SANs)">
                {fields.map((field, index) => (
                  <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <Form.Item
                      {...field}
                      style={{ flex: 1, marginBottom: 0 }}
                      rules={[{ required: true, message: '请输入域名' }]}
                    >
                      <Input placeholder="example.com 或 *.example.com" />
                    </Form.Item>
                    {fields.length > 1 && (
                      <Button
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(field.name)}
                        danger
                        type="text"
                      />
                    )}
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                  block
                >
                  添加域名
                </Button>
              </Form.Item>
            )}
          </Form.List>

          <Form.Item name="key_type" label="密钥类型">
            <Select>
              <Select.Option value="ecc256">ECC P-256 (推荐)</Select.Option>
              <Select.Option value="rsa2048">RSA 2048</Select.Option>
              <Select.Option value="rsa4096">RSA 4096</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="validity_days" label="有效期">
            <Select>
              <Select.Option value={7}>7 天</Select.Option>
              <Select.Option value={30}>30 天</Select.Option>
              <Select.Option value={90}>90 天</Select.Option>
              <Select.Option value={365}>1 年</Select.Option>
              <Select.Option value={1825}>5 年</Select.Option>
              <Select.Option value={5475}>15 年 (推荐)</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SafetyCertificateOutlined />}
              loading={originCertCreateLoading}
              block
            >
              签发证书
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Certificate Modal */}
      <Modal
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span>证书详情</span>
          </Space>
        }
        open={!!viewCertModal}
        onCancel={() => setViewCertModal(null)}
        footer={
          <Button onClick={() => setViewCertModal(null)}>关闭</Button>
        }
        width={isMobile ? '100%' : 700}
        style={isMobile ? { top: 20 } : undefined}
        styles={isMobile ? { body: { maxHeight: '70vh', overflow: 'auto' } } : undefined}
      >
        {viewCertModal && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>域名: </Text>
              <Space size={4} wrap>
                {ensureArray(viewCertModal.hostnames).map(h => (
                  <Tag key={h}>{h}</Tag>
                ))}
              </Space>
            </div>

            {viewCertModal.serial_number && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>序列号: </Text>
                <Text code>{viewCertModal.serial_number}</Text>
              </div>
            )}

            <div style={{ marginBottom: 8 }}>
              <Text strong>有效期: </Text>
              <Text>{viewCertModal.issued_at} ~ {viewCertModal.expires_at}</Text>
            </div>

            <Divider />

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong>证书 (PEM)</Text>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(viewCertModal.certificate, '证书')}
                >
                  复制
                </Button>
              </div>
              <TextArea
                value={viewCertModal.certificate}
                rows={6}
                readOnly
                style={{ fontFamily: 'monospace', fontSize: 11 }}
              />
            </div>

            {viewCertModal.private_key && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong>私钥 (PEM)</Text>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(viewCertModal.private_key, '私钥')}
                  >
                    复制
                  </Button>
                </div>
                <Alert
                  message="请妥善保管私钥，关闭此窗口后将无法再次查看。"
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                />
                <TextArea
                  value={viewCertModal.private_key}
                  rows={6}
                  readOnly
                  style={{ fontFamily: 'monospace', fontSize: 11 }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
